import { useForm } from '@tanstack/react-form'
import { useState } from 'react'
import { z } from 'zod'
import { Plus } from 'lucide-react'
import { InventoryInTransactionCreateSchema } from '../schema/inventorySchema'
import { useRecordInTransactionMutation } from '../hooks'
import { useInventoryStore } from '../store/inventoryStore'
import { inventoryUnits } from '../constants/inventory'
import { CategoryForm } from './CategoryForm'
import type { InventoryInTransactionCreateData } from '../schema/inventorySchema'
import { DragDropInput } from '@/components/custom/drag-drop-input'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { FormInput } from '@/components/custom/form-input'
import LoadingButton from '@/components/custom/loading-button'
import CurrencyInput from '@/components/custom/currency-input'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { usePermissions } from '@/utils/permissions'
import { FormTextarea } from '@/components/custom/form-textarea'
import { Modal } from '@/components/custom/modal'
import DatePicker from '@/components/custom/date-picker'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useIsMobile } from '@/hooks'
import { Button } from '@/components/ui/button'

interface InventoryInTransactionFormProps {
  trigger?: React.ReactNode
  open: boolean
  onClose: () => void
  itemId?: string | null
}

export function InventoryInTransactionForm({
  trigger,
  open,
  onClose,
  itemId,
}: InventoryInTransactionFormProps) {
  const isMobile = useIsMobile()
  const { mutateAsync: recordInTransactionMutation, isPending } =
    useRecordInTransactionMutation()

  const [openCategoryDialog, setOpenCategoryDialog] = useState(false)
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const user = useAuthStore((state) => state.user)
  const permissions = usePermissions()

  const inventoryStock = useInventoryStore((state) => state.inventoryStock)
  const categories = useInventoryStore((state) => state.categories)
  const churches = useChurchesStore((state) => state.churches)
  const userChuches = permissions.isSystemAdmin()
    ? churches
    : churches.filter((church) => user?.churchId?.includes(church._id))

  const initialItem = inventoryStock.find((item) => item._id === itemId)
  const initialChurch = churches.find(
    (church) => church._id === initialItem?.churchId,
  )

  const form = useForm({
    defaultValues: {
      type: 'IN' as const,
      churchId: initialChurch?._id || '',
      categoryId: initialItem?.categoryId || '',
      item: initialItem?.item || '',
      unit: initialItem?.unit || '',
      brand: initialItem?.brand || '',
      description: initialItem?.description || '',
      quantity: 1,
      acquisition: {
        price: undefined as number | undefined,
        purchaseDate: '',
        invoiceNumber: '',
        provider: '',
      },
      notes: '',
      inventoryStockId: initialItem?._id || '',
    } as InventoryInTransactionCreateData,
    onSubmit: async ({ value }) => {
      await recordInTransactionMutation({
        transaction: value,
        invoiceFile: invoiceFile || undefined,
      })
      handleClose()
    },
  })

  const handleClose = () => {
    form.reset()
    setPreviewUrl(null)
    onClose()
  }

  const handleFileSelect = (file: File | null) => {
    if (file) {
      setInvoiceFile(file)
      setPreviewUrl(URL.createObjectURL(file))
    } else {
      setInvoiceFile(null)
      setPreviewUrl(null)
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      trigger={trigger}
      title="Add Inventory"
      direction="right"
      contentClassName={`${isMobile ? 'min-w-[98vw]' : 'min-w-[70vw]'} ${isMobile ? 'max-h-full' : 'max-h-[90vh]'} flex flex-col overflow-y-auto`}
    >
      <ScrollArea className={`max-h-[90vh]`}>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-6 py-4"
        >
          <form.Field
            name="churchId"
            children={(field) => {
              return (
                <div className="gap-0">
                  <AutoCompleteSelect
                    field={field}
                    label="Church"
                    id={field.name}
                    name={field.name}
                    options={userChuches.map((church) => ({
                      value: church._id,
                      label: church.name,
                    }))}
                    required
                    disabled={isPending}
                  />
                </div>
              )
            }}
          />
          <form.Subscribe
            selector={(state) => state.values.churchId}
            children={(churchId) => {
              const isFieldsDisabled = !churchId || isPending

              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Item Select */}
                  <form.Field
                    name="item"
                    validators={{
                      onBlur: ({ value }) => {
                        const result =
                          InventoryInTransactionCreateSchema.shape.item.safeParse(
                            value,
                          )
                        return result.success
                          ? undefined
                          : result.error.errors[0].message
                      },
                    }}
                    children={(field) => {
                      return (
                        <div className="gap-0">
                          <FormInput
                            field={field}
                            label="Item"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isFieldsDisabled}
                            required
                          />
                        </div>
                      )
                    }}
                  />

                  {!itemId && (
                    <form.Field
                      name="brand"
                      validators={{
                        onBlur: ({ value }) => {
                          const result =
                            InventoryInTransactionCreateSchema.shape.brand.safeParse(
                              value,
                            )
                          return result.success
                            ? undefined
                            : result.error.errors[0].message
                        },
                      }}
                      children={(field) => (
                        <div className="gap-0">
                          <FormInput
                            field={field}
                            label="Brand"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isFieldsDisabled}
                          />
                        </div>
                      )}
                    />
                  )}

                  {!itemId && (
                    <form.Field
                      name="categoryId"
                      validators={{
                        onBlur: ({ value }) => {
                          const result =
                            InventoryInTransactionCreateSchema.shape.categoryId.safeParse(
                              value,
                            )
                          return result.success
                            ? undefined
                            : result.error.errors[0].message
                        },
                        onChange: ({ value }) => {
                          const result =
                            InventoryInTransactionCreateSchema.shape.categoryId.safeParse(
                              value,
                            )
                          return result.success
                            ? undefined
                            : result.error.errors[0].message
                        },
                      }}
                      children={(field) => {
                        return (
                          <div className="gap-0">
                            <AutoCompleteSelect
                              field={field}
                              label="Category"
                              id={field.name}
                              name={field.name}
                              options={categories.map((cat) => ({
                                value: cat._id,
                                label: cat.name,
                                description: cat.description,
                              }))}
                              disabled={isFieldsDisabled}
                              required
                              addDialog={
                                <CategoryForm
                                  type="add"
                                  onClose={() => setOpenCategoryDialog(false)}
                                  open={openCategoryDialog}
                                  trigger={
                                    <Button
                                      onClick={() => {
                                        setOpenCategoryDialog(true)
                                      }}
                                    >
                                      <Plus /> Add Category
                                    </Button>
                                  }
                                />
                              }
                            />
                          </div>
                        )
                      }}
                    />
                  )}

                  {!itemId && (
                    <form.Field
                      name="unit"
                      validators={{
                        onBlur: ({ value }) => {
                          const result =
                            InventoryInTransactionCreateSchema.shape.unit.safeParse(
                              value,
                            )
                          return result.success
                            ? undefined
                            : result.error.errors[0].message
                        },
                        onChange: ({ value }) => {
                          const result =
                            InventoryInTransactionCreateSchema.shape.unit.safeParse(
                              value,
                            )
                          return result.success
                            ? undefined
                            : result.error.errors[0].message
                        },
                      }}
                      children={(field) => {
                        return (
                          <div className="gap-0">
                            <AutoCompleteSelect
                              field={field}
                              label="Unit of Measurement"
                              id={field.name}
                              name={field.name}
                              options={inventoryUnits.map((unit) => ({
                                value: unit,
                                label: unit,
                              }))}
                              placeholder="e.g., pcs, meters, boxes, kg"
                              disabled={isFieldsDisabled}
                              required
                            />
                          </div>
                        )
                      }}
                    />
                  )}

                  {!itemId && (
                    <form.Field
                      name="description"
                      validators={{
                        onBlur: ({ value }) => {
                          const result =
                            InventoryInTransactionCreateSchema.shape.description.safeParse(
                              value,
                            )
                          return result.success
                            ? undefined
                            : result.error.errors[0].message
                        },
                      }}
                      children={(field) => (
                        <div className="gap-0">
                          <FormInput
                            field={field}
                            label="Description"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isFieldsDisabled}
                          />
                        </div>
                      )}
                    />
                  )}

                  {/* Quantity */}
                  <form.Field
                    name="quantity"
                    validators={{
                      onBlur: ({ value }) => {
                        const result =
                          InventoryInTransactionCreateSchema.shape.quantity.safeParse(
                            +value,
                          )
                        return result.success
                          ? undefined
                          : result.error.errors[0].message
                      },
                    }}
                    children={(field) => (
                      <div className="gap-0">
                        <FormInput
                          field={field}
                          label="Quantity"
                          type="number"
                          min="1"
                          value={field.state.value}
                          onChange={(e) =>
                            field.handleChange(parseFloat(e.target.value) || 1)
                          }
                          onBlur={field.handleBlur}
                          disabled={isFieldsDisabled}
                          required
                        />
                      </div>
                    )}
                  />
                </div>
              )
            }}
          />

          {/* Acquisition Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Acquisition Details</h3>
            <form.Subscribe
              selector={(state) => state.values.churchId}
              children={(churchId) => {
                const isFieldsDisabled = !churchId || isPending

                return (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {/* Price */}
                    <form.Field
                      name="acquisition.price"
                      validators={{
                        onBlur: ({ value }) => {
                          if (!value) return undefined
                          const result = z
                            .number()
                            .min(0, 'Price cannot be negative')
                            .safeParse(value)
                          return result.success
                            ? undefined
                            : result.error.errors[0].message
                        },
                      }}
                      children={(field) => (
                        <div className="gap-0">
                          <CurrencyInput
                            field={field}
                            label="Price"
                            id={field.name}
                            name={field.name}
                            className="text-right"
                            disabled={isFieldsDisabled}
                          />
                        </div>
                      )}
                    />

                    <form.Field
                      name="acquisition.purchaseDate"
                      children={(field) => (
                        <DatePicker
                          field={field}
                          className="w-full"
                          label="Purchase Date"
                          startYear={2000}
                          disableFutureDates
                          disabled={isFieldsDisabled}
                          id={field.name}
                          name={field.name}
                        />
                      )}
                    />

                    {/* Invoice Number */}
                    <form.Field
                      name="acquisition.invoiceNumber"
                      children={(field) => (
                        <div className="gap-0">
                          <FormInput
                            field={field}
                            label="Invoice Number"
                            type="text"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            placeholder="Enter invoice number"
                            disabled={isFieldsDisabled}
                          />
                        </div>
                      )}
                    />

                    {/* Provider */}
                    <form.Field
                      name="acquisition.provider"
                      children={(field) => (
                        <div className="gap-0">
                          <FormInput
                            field={field}
                            label="Provider / Supplier"
                            type="text"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            placeholder="Enter provider name"
                            disabled={isFieldsDisabled}
                          />
                        </div>
                      )}
                    />
                  </div>
                )
              }}
            />
          </div>

          <form.Subscribe
            selector={(state) => state.values.churchId}
            children={(churchId) => {
              const isFieldsDisabled = !churchId || isPending
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DragDropInput
                    onFileSelect={handleFileSelect}
                    selectedFile={invoiceFile}
                    accept="image/*,application/pdf"
                    label="Invoice / Purchase Receipt"
                    previewUrl={previewUrl}
                    disabled={isFieldsDisabled}
                  />
                  <form.Field
                    name="notes"
                    children={(field) => (
                      <FormTextarea
                        field={field}
                        label="Notes"
                        id={field.name}
                        name={field.name}
                        disabled={isFieldsDisabled}
                      />
                    )}
                  />
                </div>
              )
            }}
          />

          {/* Submit Button */}
          <form.Subscribe
            selector={(state) => state.values.churchId}
            children={(churchId) => {
              const isFieldsDisabled = !churchId || isPending

              return (
                <div className="flex gap-2 justify-end">
                  <LoadingButton
                    isLoading={isPending}
                    disabled={isFieldsDisabled}
                    type="submit"
                  >
                    {isPending ? 'Submitting...' : 'Submit'}
                  </LoadingButton>
                </div>
              )
            }}
          />
        </form>
      </ScrollArea>
    </Modal>
  )
}
